import { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getOptimizationTargetKey } from "../../predict/predictUtils";
import { formatSuggestedSettings } from "../../../utils/Utils";

const ExportAllData = () => {
  const { t, i18n } = useTranslation();
  const cleanData = useSelector((state) => state.cleanData);
  const water_type = useSelector((state) => state.water_type);
  const ro_nf_elements_used = useSelector((state) => state.ro_nf_elements_used);
  const ro_nf_elements_number = useSelector(
    (state) => state.ro_nf_elements_number,
  );
  const ro_nf_stages = useSelector((state) => state.ro_nf_stages);
  const installation_date = useSelector((state) => state.installation_date);
  const warranty = useSelector((state) => state.warranty);

  const flow = useSelector((state) => state.flow);
  const pressure = useSelector((state) => state.pressure);
  const feed = useSelector((state) => state.feed_quality);
  const permeate = useSelector((state) => state.permeate_quality);
  const temperature = useSelector((state) => state.temperature);
  const flux = useSelector((state) => state.flux);
  const volume = useSelector((state) => state.volume);
  const currency = useSelector((state) => state.currency);
  const mass = useSelector((state) => state.mass);
  const conversion_rate = useSelector((state) => state.conversion_rate);

  const max_feed_pressure = useSelector((state) => state.max_feed_pressure);
  const max_permeate_quality = useSelector(
    (state) => state.max_permeate_quality,
  );
  const std_warranty_period = useSelector((state) => state.std_warranty_period);
  const startup_pressure_drop = useSelector(
    (state) => state.startup_pressure_drop,
  );
  const designed_permeate_flow = useSelector(
    (state) => state.designed_permeate_flow,
  );
  const std_membrane_age = useSelector((state) => state.std_membrane_age);
  const max_pressure_drop = useSelector((state) => state.max_pressure_drop);
  const min_permeate_flow = useSelector((state) => state.min_permeate_flow);
  const plantName = useSelector((state) => state.plantName);
  const plantLocation = useSelector((state) => state.plantLocation);
  const num_trains = useSelector((state) => state.num_trains);
  const dataset_name = useSelector((state) => state.dataset_name);
  const tol_num_elements = useSelector((state) => state.tol_num_elements);
  const is_dupont_element = useSelector((state) => state.is_dupont_element);
  const sel_pass = useSelector((state) => state.sel_pass);
  const sel_train = useSelector((state) => state.sel_train);
  const sel_stage = useSelector((state) => state.sel_stage);
  const sel_pressure_vessels = useSelector(
    (state) => state.sel_pressure_vessels,
  );
  const sel_element_involved = useSelector(
    (state) => state.sel_element_involved,
  );
  const feed_water_factor = useSelector((state) => state.feed_water_factor);
  const feed_water_permeate_factor = useSelector(
    (state) => state.feed_water_permeate_factor,
  );
  const sel_element_type = useSelector((state) => state.sel_element_type);
  const clusterPoints = useSelector((state) => state.clusterPoints);
  const medianCurvePoints = useSelector((state) => state.medianCurvePoints);
  const maxRelativeDistance = useSelector((state) => state.maxRelativeDistance);
  const permeateFlowDropThreshold = useSelector(
    (state) => state.permeateFlowDropThreshold,
  );
  const pressureDropThreshold = useSelector(
    (state) => state.pressureDropThreshold,
  );
  const pressureDropThresholdAbsolute = useSelector(
    (state) => state.pressureDropThresholdAbsolute,
  );
  const useSaltPassageChange = useSelector(
    (state) => state.useSaltPassageChange,
  );
  const saltPassageIncreaseThreshold = useSelector(
    (state) => state.saltPassageIncreaseThreshold,
  );
  const minDaysBetweenCIPs = useSelector((state) => state.minDaysBetweenCIPs);
  const maxDaysBetweenCIPs = useSelector((state) => state.maxDaysBetweenCIPs);
  const cipIdentificationResolution = useSelector(
    (state) => state.cipIdentificationResolution,
  );
  const minIdleDaysShutdown = useSelector((state) => state.minIdleDaysShutdown);
  const maxStabilizationDaysAfterCIP = useSelector(
    (state) => state.maxStabilizationDaysAfterCIP,
  );
  const minStabilizationDaysAfterCIP = useSelector(
    (state) => state.minStabilizationDaysAfterCIP,
  );
  const minPointsForCleaningEffectiveness = useSelector(
    (state) => state.minPointsForCleaningEffectiveness,
  );
  const deafultStabilizationDaysAfterCIP = useSelector(
    (state) => state.deafultStabilizationDaysAfterCIP,
  );
  const foulingAggressivenessFactor = useSelector(
    (state) => state.foulingAggressivenessFactor,
  );
  const numberOfIterationsToFindOptima = useSelector(
    (state) => state.numberOfIterationsToFindOptima,
  );
  const adaptPermeateFlowDropThreshold = useSelector(
    (state) => state.adaptPermeateFlowDropThreshold,
  );
  const adaptPressureDropThreshold = useSelector(
    (state) => state.adaptPressureDropThreshold,
  );
  const adaptSaltPassageThreshold = useSelector(
    (state) => state.adaptSaltPassageThreshold,
  );
  const simulationImproveAccuracy = useSelector(
    (state) => state.simulationImproveAccuracy,
  );
  const forecast_window = useSelector((state) => state.forecast_window);
  const failureProbabilityThreshold = useSelector(
    (state) => state.failureProbabilityThreshold,
  );
  const suggestedSettingsRaw = useSelector((state) => state.suggestedSettings);
  const { optimizationTarget } = useSelector((state) => state);

  // Format suggested settings using shared utility function
  const suggestedOthers = formatSuggestedSettings(suggestedSettingsRaw, t);

  const [exportAllData, setExportAllData] = useState({
    others: [
      {
        plantName,
        plantLocation,
        water_type,
        num_trains,
        tol_num_elements,
        is_dupont_element: is_dupont_element
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        dataset_name,
        installation_date,
        warranty,
        sel_train,
        sel_pass,
        sel_stage,
        sel_pressure_vessels,
        sel_element_involved,
        sel_element_type,
        flow,
        pressure,
        feed,
        feed_water_factor,
        permeate,
        feed_water_permeate_factor,
        temperature: `\u00B0${temperature}`,
        flux,
        volume,
        currency,
        mass,
        conversion_rate,
        max_feed_pressure,
        max_permeate_quality,
        std_warranty_period,
        startup_pressure_drop,
        designed_permeate_flow,
        std_membrane_age,
        max_pressure_drop,
        min_permeate_flow,
        clusterPoints,
        medianCurvePoints,
        maxRelativeDistance,
        permeateFlowDropThreshold,
        pressureDropThreshold,
        pressureDropThresholdAbsolute,
        useSaltPassageChange: useSaltPassageChange
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        saltPassageIncreaseThreshold,
        minDaysBetweenCIPs,
        maxDaysBetweenCIPs,
        cipIdentificationResolution,
        minIdleDaysShutdown,
        maxStabilizationDaysAfterCIP,
        minStabilizationDaysAfterCIP,
        minPointsForCleaningEffectiveness,
        deafultStabilizationDaysAfterCIP,
        foulingAggressivenessFactor,
        numberOfIterationsToFindOptima,
        adaptPermeateFlowDropThreshold: adaptPermeateFlowDropThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        adaptPressureDropThreshold: adaptPressureDropThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        adaptSaltPassageThreshold: adaptSaltPassageThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        simulationImproveAccuracy: simulationImproveAccuracy
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        forecast_window,
        failureProbabilityThreshold,
        optimizationTarget: optimizationTarget
          ? t(`advanceSettings.${getOptimizationTargetKey(optimizationTarget)}`)
          : "",
      }, //keep the order in sync with OthersTableStrings
    ],
    suggestedOthers,
    cleanData,
  });

  return exportAllData;
};

export default ExportAllData;
